import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../../config/api";
import { useLocation, useNavigate } from "react-router-dom";

export default function OrderDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const orderno = state?.orderno;
  const [downloadedFiles, setDownloadedFiles] = useState([]);

  // State to track if browser blocked the batch download
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);

  // ✅ FETCH ORDER DETAILS
  const fetchOrderDetails = useCallback(() => {
    if (!orderno) return;

    setLoading(true);

    fetch(
      `https://lifetimedesign.in/LtcJsonAPI.asmx/OrderDetails?orderno=${orderno}`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data.data) {
          setOrder({
            info: data.data.order?.[0],
            items: data.data.detail || [],
          });
        } else {
          setOrder(null);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setOrder(null);
      })
      .finally(() => setLoading(false));
  }, [orderno]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  if (!orderno) {
    return <div className="text-center mt-5">❌ Order number missing</div>;
  }

  if (loading) {
    return <div className="text-center mt-5">⏳ Loading order details...</div>;
  }

  if (!order) {
    return <div className="text-center mt-5">❌ Order not found</div>;
  }

  const isConfirmed = order.info?.isconfirmed === 1;
  const totalDesigns = order.items?.length || 0;

  const totalAmount = order.items?.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0,
  );

  const breadcrumbItems = [
    {
      label: (
        <>
          <i className="bi bi-house"></i> Home
        </>
      ),
      link: "/",
    },
    { label: "My Orders", link: "/myorders" },
    { label: "Order Details", link: "/order-details" },
  ];

  // ✅ FIXED SINGLE DOWNLOAD (NO TABS)
  const handleDownload = (fileName) => {
    const downloadUrl = `${BASE_URL}/download.php?file=${encodeURIComponent(fileName)}`;

    const link = document.createElement("a");

    link.href = downloadUrl;

    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };
  const handleDownloadAll = async () => {
    const files = order.items
      .filter((item) => item.emb_file)
      .map((item) => item.emb_file);

    if (!files.length) {
      alert("No files available");
      return;
    }

    setShowPermissionWarning(true);

    files.forEach((file, index) => {
      // keep original server file
      const serverFile = file;

      // browser download name
      const downloadName = file.endsWith(".txt")
        ? file.replace(".txt", ".emb")
        : file;

      const url =
        `${BASE_URL}/download.php?file=` + encodeURIComponent(serverFile);

      setTimeout(() => {
        const a = document.createElement("a");

        a.href = url;

        // force final downloaded name
        a.download = downloadName;

        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);
      }, index * 2000); // 2 sec gap
    });

    setDownloadedFiles(files);
  };
  return (
    <div className="wish-page" style={{ paddingTop: "20px" }}>
      {/* HEADER */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          {breadcrumbItems.map((item, i) => (
            <li
              key={i}
              className={`breadcrumb-item ${i === breadcrumbItems.length - 1 ? "active" : ""}`}
            >
              {i === breadcrumbItems.length - 1 ? (
                item.label
              ) : (
                <a href={item.link}>{item.label}</a>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* ✅ PERMISSION WARNING BANNER */}
      {showPermissionWarning && (
        <div
          className="alert alert-warning alert-dismissible fade show shadow-sm mb-3"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>Downloading Multiple Files:</strong> If your files stop saving
          automatically, look at the right side of your browser address bar and
          choose <strong>"Always allow automatic downloads"</strong>.
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowPermissionWarning(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <div className="card mb-4 p-3 shadow-sm border-0">
        <div className="d-flex justify-content-between mb-4">
          <h4 className="fw-bold mb-3">Order Information</h4>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/myorders")}
          >
            ← Back
          </button>
        </div>

        <div className="row">
          <div className="col-md-3 mb-2">
            <h5>Order No: {orderno}</h5>
          </div>
          <div className="col-md-3 mb-2">
            <h5>Total Designs: {totalDesigns}</h5>
          </div>
          <div className="col-md-3 mb-2">
            <h5>Total Amount: ₹{totalAmount}</h5>
          </div>
          <div className="col-md-3 mb-2">
            <h5>Order Date: {order.info?.orderdate || "-"} </h5>
          </div>
          <div className="d-flex justify-content-end mb-3">
            {isConfirmed && (
              <button className="btn btn-primary" onClick={handleDownloadAll}>
                <i className="bi bi-download me-2"></i>
                Download All Files
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-dark">
            <tr>
              <th className="text-center">#</th>
              <th className="text-center">Image</th>
              <th>Design Info</th>
              <th className="text-center">Amount</th>
              <th className="text-center">Status</th>
              <th className="text-center">Download</th>
            </tr>
          </thead>

          <tbody className="table-body">
            {order.items?.map((item, index) => {
              const fileName = item.emb_file?.endsWith(".txt")
                ? item.emb_file.replace(".txt", ".emb")
                : item.emb_file;

              return (
                <tr key={index}>
                  <td className="text-center">{index + 1}</td>
                  <td className="text-center">
                    <img
                      src={`https://lifetimedesign.in/controlpanel/Designs/${item.image_url}`}
                      alt=""
                      style={{
                        width: "100px",
                        height: "150px",
                        objectFit: "contain",
                      }}
                    />
                  </td>
                  <td>
                    <div className="design-info">
                      <div>
                        <strong>Code:</strong> {item.code}
                      </div>
                      <div>
                        <strong>Stitches:</strong> {item.stiches}
                      </div>
                      <div>
                        <strong>Area:</strong> {item.area}
                      </div>
                      <div>
                        <strong>Color:</strong> {item.color}
                      </div>
                      <div>
                        <strong>Concept:</strong> {item.concept}
                      </div>
                      <div>
                        <strong>Height:</strong> {item.packageno}
                      </div>
                      <div>
                        <strong>Width:</strong> {item.sequence}
                      </div>
                      <div>
                        <strong>Details:</strong> {item.description}
                      </div>
                    </div>
                  </td>
                  <td className="fw-bold text-primary text-center">
                    ₹{item.amount}
                  </td>
                  <td className="text-center">
                    {isConfirmed ? (
                      <span className="text-success fs-4">
                        <i className="bi bi-check-circle-fill"></i>
                      </span>
                    ) : (
                      <span className="text-danger fs-4">
                        <i className="bi bi-x-circle-fill"></i>
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    {isConfirmed && fileName ? (
                      <button
                        className={`btn btn-sm ${
                          downloadedFiles.includes(item.emb_file)
                            ? "btn-secondary"
                            : "btn-success"
                        }`}
                        disabled={downloadedFiles.includes(item.emb_file)}
                        onClick={() => handleDownload(item.emb_file)}
                      >
                        {downloadedFiles.includes(item.emb_file) ? (
                          <>
                            <i className="bi bi-check-circle-fill me-1"></i>
                            Downloaded
                          </>
                        ) : (
                          <>
                            <i className="bi bi-download me-1"></i>
                            Download
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
